import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import './AdminServicesOverview.css';

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
const fmt = (n) => Number(n || 0).toLocaleString();

// Jump to the SMM tab, optionally preselecting a provider to manage.
const goManage = (provider) => {
  window.dispatchEvent(new CustomEvent('admin:setTab', { detail: { tab: 'smm' } }));
  if (provider) window.dispatchEvent(new CustomEvent('smm:selectProvider', { detail: { provider } }));
};

const AdminServicesOverview = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // provider object for the drill-down modal

  const load = useCallback(async () => {
    try {
      const res = await api.get('/smm/admin/services/overview');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load services overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="aso-wrap"><div className="aso-muted">Loading services overview…</div></div>;
  if (error && !data) return <div className="aso-wrap"><div className="aso-error">⚠ {error}</div></div>;
  if (!data) return null;

  const {
    total_catalog: catalog, total_enabled: enabled, total_custom_priced: customPriced,
    total_default_priced: defaultPriced, default_markup_x: markupX, providers = [],
  } = data;

  return (
    <div className="aso-wrap">
      <div className="aso-head">
        <h3>Services Overview</h3>
        <button className="aso-refresh" onClick={load}>↻ Refresh</button>
      </div>

      {/* Headline tiles — clickable */}
      <div className="aso-tiles">
        <button className="aso-tile aso-click" onClick={() => goManage()} title="Manage services">
          <span className="aso-tile-lbl">Services we can offer</span>
          <span className="aso-tile-val">{fmt(catalog)}</span>
          <span className="aso-tile-sub">across {providers.length} provider{providers.length === 1 ? '' : 's'} ↗</span>
        </button>
        <button className="aso-tile aso-tile--ok aso-click" onClick={() => goManage()} title="Manage enabled services">
          <span className="aso-tile-lbl">Enabled (live to users)</span>
          <span className="aso-tile-val">{fmt(enabled)} <small>/ {fmt(catalog)}</small></span>
          <span className="aso-tile-sub">{pct(enabled, catalog)}% of catalog ↗</span>
        </button>
        <div className="aso-tile aso-tile--off">
          <span className="aso-tile-lbl">Not offered</span>
          <span className="aso-tile-val">{fmt(catalog - enabled)}</span>
          <span className="aso-tile-sub">disabled / not enabled</span>
        </div>
        <div className="aso-tile aso-tile--gold">
          <span className="aso-tile-lbl">Default markup</span>
          <span className="aso-tile-val">{markupX}×</span>
          <span className="aso-tile-sub">auto-applied unless you set a price</span>
        </div>
      </div>

      {/* Enabled vs catalog bar */}
      {catalog > 0 && (
        <div className="aso-bar" title={`${fmt(enabled)} enabled · ${fmt(catalog - enabled)} not offered`}>
          <div className="aso-bar-on" style={{ width: `${pct(enabled, catalog)}%` }} />
          <div className="aso-bar-off" style={{ width: `${100 - pct(enabled, catalog)}%` }} />
        </div>
      )}

      {/* Pricing health */}
      <div className="aso-pricing">
        💰 Of <strong>{fmt(enabled)}</strong> enabled services, <strong>{fmt(defaultPriced)}</strong> use the default <strong>{markupX}× markup</strong>
        {customPriced > 0 && <> and <strong>{fmt(customPriced)}</strong> use a custom price you set</>}.
        Every enabled service stays profitable by default — you only override pricing when you choose to.
      </div>

      {/* Per-provider cards — click for a deeper glance */}
      <div className="aso-section-label">By provider — click a card for the breakdown</div>
      <div className="aso-prov-grid">
        {providers.map((p) => (
          <button key={p.provider} className="aso-prov-card aso-click" onClick={() => setDrill(p)} title="See platform breakdown">
            <div className="aso-prov-head">
              <strong>{p.label}</strong>
              <span className="aso-prov-id">{p.provider}</span>
            </div>
            <div className="aso-prov-big">{fmt(p.enabled)} <small>/ {fmt(p.catalog)}</small></div>
            <div className="aso-prov-sub">enabled · {pct(p.enabled, p.catalog)}%</div>
            <div className="aso-bar sm">
              <div className="aso-bar-on" style={{ width: `${pct(p.enabled, p.catalog)}%` }} />
              <div className="aso-bar-off" style={{ width: `${100 - pct(p.enabled, p.catalog)}%` }} />
            </div>
            <div className="aso-prov-foot">
              {p.custom_priced > 0 ? `${fmt(p.custom_priced)} custom-priced` : 'all at 2× default'} · view →
            </div>
          </button>
        ))}
        {providers.length === 0 && (
          <div className="aso-muted">No providers connected.</div>
        )}
      </div>

      {/* Drill-down modal: platform breakdown for one provider */}
      {drill && (
        <div className="aso-overlay" onClick={() => setDrill(null)}>
          <div className="aso-modal" onClick={(e) => e.stopPropagation()}>
            <button className="aso-close" onClick={() => setDrill(null)} aria-label="Close">×</button>
            <span className="aso-eyebrow">{drill.label} · {drill.provider}</span>
            <h3 className="aso-modal-title">{fmt(drill.enabled)} / {fmt(drill.catalog)} services enabled</h3>
            <div className="aso-table-wrap">
              <table className="aso-table">
                <thead><tr><th>Platform</th><th>Enabled</th><th>Catalog</th><th>%</th></tr></thead>
                <tbody>
                  {(drill.by_platform || []).map((pl) => (
                    <tr key={pl.platform}>
                      <td><strong>{pl.platform}</strong></td>
                      <td className="aso-ok">{fmt(pl.enabled)}</td>
                      <td>{fmt(pl.catalog)}</td>
                      <td>{pct(pl.enabled, pl.catalog)}%</td>
                    </tr>
                  ))}
                  {(drill.by_platform || []).length === 0 && (
                    <tr><td colSpan="4" className="aso-muted" style={{ textAlign: 'center' }}>No services.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <button className="aso-manage" onClick={() => { goManage(drill.provider); setDrill(null); }}>
              Manage {drill.label}’s services →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServicesOverview;
